/**
 * Runtime Event Trace — bounded append-only event log for local browser debugging.
 *
 * Built as a companion to runtimeDebugSnapshot.ts (§134). Where snapshots answer
 * "what does the world look like now?", the trace answers "how did we get here?".
 *
 * Design:
 * - Ring buffer: max 500 entries (configurable). Old entries are evicted silently.
 * - Redaction: token fragments, secrets, large blobs, and capability payloads are
 *   replaced with safe placeholders. No raw secrets ever enter the log.
 * - Kinds: identity:changed | workspace:switched | workspace:sync-status |
 *   workspace:deleted | registry:sync-status | sqlite3mt:debug-stage |
 *   sqlite-write-lane | sqlite-lane-state
 */

import type { EngineManagerEventPayload } from '../engine/types'

// EngineManager is re-exported via testMode.ts installTestMode for use in installRuntimeEventTrace
// Using `any` for listener payload to accommodate EngineManager's more specific event type (contravariance)
type EngineManagerForTrace = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    on(event: string, listener: (payload: any) => void): () => void
}

/* ─── Types ──────────────────────────────────────────────────────────── */

export type RuntimeEventKind =
    | 'identity:changed'
    | 'workspace:switched'
    | 'workspace:sync-status'
    | 'workspace:deleted'
    | 'registry:sync-status'
    | 'sqlite3mt:debug-stage'
    | 'sqlite-write-lane'
    | 'sqlite-lane-state'

export interface RuntimeEventEntry {
    /** Monotonically increasing integer id. */
    id: number
    /** ISO-8601 timestamp. */
    timestamp: string
    /** Event kind discriminator. */
    kind: RuntimeEventKind
    /**
     * Redacted event data.
     * - Token/secret strings → '[REDACTED]'
     * - ArrayBuffer/Uint8Array → '[...N bytes]'
     * - Large objects (> 200 chars serialized) → '[large payload]'
     * - Specific key patterns (token, secret, key, password, seed) → values redacted
     */
    data: Record<string, unknown>
    /** Where the event originated: 'engine' | 'sqlite3mt' */
    source: 'engine' | 'sqlite3mt'
}

/** Options for getRuntimeEventTrace(). */
export interface GetRuntimeEventTraceOptions {
    /** Filter by event kinds. */
    kinds?: RuntimeEventKind[]
    /** Return only events after this event id. */
    since?: number
    /** Maximum number of entries to return (default 500). */
    limit?: number
}

/* ─── Redaction helpers ─────────────────────────────────────────────── */

const LARGE_PAYLOAD_THRESHOLD = 200
const REDACT_KEY_PATTERNS = /token|secret|key|password|seed|credential|auth|Bearer|Basic|token=|secret=/i

function shouldRedactKey(key: string): boolean {
    return REDACT_KEY_PATTERNS.test(key)
}

function redactValue(value: unknown): unknown {
    if (value === null || value === undefined) return value
    if (typeof value === 'string') {
        if (value.length > LARGE_PAYLOAD_THRESHOLD * 2) return '[large string]'
        return value
    }
    if (typeof value === 'number' || typeof value === 'boolean') return value
    if (value instanceof Uint8Array || value instanceof ArrayBuffer) {
        return `[...${value.byteLength} bytes]`
    }
    if (Array.isArray(value)) return value.map(redactValue)
    if (typeof value === 'object') {
        const result: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            result[k] = shouldRedactKey(k) ? '[REDACTED]' : redactValue(v)
        }
        return result
    }
    return value
}

function redactPayload(payload: unknown): Record<string, unknown> {
    if (payload === null || payload === undefined) return {}
    if (typeof payload !== 'object') return { value: String(payload) }
    return redactValue(payload) as Record<string, unknown>
}

/* ─── Batch flush state (§263) ──────────────────────────────────────── */

/**
 * §263: Incremental flush writer type.
 * Called with a JSON-serialised array of RuntimeEventEntry (newest-last)
 * every time the batch flushes. Consumers write this directly to the probe file
 * so ComponentJobPanel can poll it during job execution.
 */
export type RuntimeEventBatchWriter = (serialisedEntries: string) => void

interface BatchFlushConfig {
    /** Flush after this many new events have been accumulated (default 10). */
    eventCountThreshold?: number
    /** Flush after this many milliseconds since the first unflushed event (default 500). */
    flushIntervalMs?: number
}

let _batchWriter: RuntimeEventBatchWriter | null = null
let _batchConfig: BatchFlushConfig = {}
let _pendingBatch: RuntimeEventEntry[] = []
let _batchTimer: ReturnType<typeof setTimeout> | null = null
let _batchLastId = 0

function flushPendingBatch(): void {
    if (_pendingBatch.length === 0 || !_batchWriter) return

    const entries = _pendingBatch.splice(0)
    _batchLastId = entries[entries.length - 1]?.id ?? _batchLastId
    try {
        _batchWriter(JSON.stringify(entries))
    } catch {
        // Best-effort: writer errors must not crash the event path.
    }
}

function scheduleBatchTimer(): void {
    if (_batchTimer !== null) return
    const interval = _batchConfig.flushIntervalMs ?? 500
    _batchTimer = setTimeout(() => {
        _batchTimer = null
        flushPendingBatch()
    }, interval)
}

function cancelBatchTimer(): void {
    if (_batchTimer !== null) {
        clearTimeout(_batchTimer)
        _batchTimer = null
    }
}

/**
 * §263: Start incremental batch-flush mode.
 *
 * In this mode every call to `enqueueRuntimeEventBatch` (not plain `appendRuntimeEvent`)
 * is also queued for an incremental file flush. Flushing happens either:
 * - After `eventCountThreshold` events have been queued (default 10), or
 * - After `flushIntervalMs` milliseconds have elapsed since the first unflushed event
 *   (default 500 ms).
 *
 * This allows the probe file to be written incrementally during job execution so
 * ComponentJobPanel can poll it and render a live trace panel without waiting for
 * the run to complete.
 *
 * Only one batch writer can be active at a time. Calling this again replaces the
 * previous writer and resets all pending state.
 *
 * @param writer  Called with a JSON string of RuntimeEventEntry[] each flush.
 * @param config  Optional flush thresholds.
 */
export function startRuntimeEventBatchFlush(
    writer: RuntimeEventBatchWriter,
    config: BatchFlushConfig = {},
): void {
    stopRuntimeEventBatchFlush()
    _batchWriter = writer
    _batchConfig = { eventCountThreshold: 10, flushIntervalMs: 500, ...config }
    _pendingBatch = []
    _batchLastId = _nextId - 1
}

/**
 * §263: Stop incremental batch-flush mode and cancel any pending timer.
 * The ring buffer is NOT cleared.
 */
export function stopRuntimeEventBatchFlush(): void {
    cancelBatchTimer()
    // Flush any remaining events before stopping.
    flushPendingBatch()
    _batchWriter = null
    _pendingBatch = []
    _batchConfig = {}
    _batchLastId = 0
}

/**
 * §263: Flush the current pending batch immediately (drains the timer).
 * Safe to call even when batch mode is not active (no-op).
 */
export function flushRuntimeEventBatch(): void {
    cancelBatchTimer()
    flushPendingBatch()
}

/**
 * §263: Record an event into both the ring buffer AND the incremental flush batch.
 *
 * Unlike plain `appendRuntimeEvent`, this variant is designed for live probe
 * streaming: it feeds the same ring buffer but also queues the event for the
 * incremental file writer started via `startRuntimeEventBatchFlush`.
 *
 * The event is flushed (written to the probe file) either after
 * `eventCountThreshold` events or after `flushIntervalMs` ms — whichever comes first.
 *
 * If no batch writer is active, this is equivalent to plain `appendRuntimeEvent`.
 */
/**
 * §263: Record a single event into both the ring buffer AND the incremental flush batch.
 *
 * Unlike plain `appendRuntimeEvent`, this variant is designed for live probe
 * streaming: it feeds the same ring buffer but also queues the event for the
 * incremental file writer started via `startRuntimeEventBatchFlush`.
 *
 * The event is flushed (written to the probe file) either after
 * `eventCountThreshold` events or after `flushIntervalMs` ms — whichever comes first.
 *
 * If no batch writer is active, this is equivalent to plain `appendRuntimeEvent`.
 *
 * Note: this function is named `enqueueRuntimeEventBatch` to avoid a signature
 * conflict with the `appendRuntimeEventBatch(entries[])` overload used by the
 * probe file flush path.
 */
export function enqueueRuntimeEventBatch(
    kind: RuntimeEventKind,
    payload: EngineManagerEventPayload | Record<string, unknown> | undefined,
    source: 'engine' | 'sqlite3mt',
): void {
    // Write to the ring buffer for post-run probe captures.
    // Do NOT call appendRuntimeEvent() here — that would double-increment _nextId.
    const entry: RuntimeEventEntry = {
        id: _nextId++,
        timestamp: new Date().toISOString(),
        kind,
        data: redactPayload(payload as Record<string, unknown>),
        source,
    }
    _entries.push(entry)
    if (_entries.length > _maxEntries) {
        _entries = _entries.slice(_entries.length - _maxEntries)
    }

    if (!_batchWriter) return
    _pendingBatch.push(entry)

    if (_pendingBatch.length >= (_batchConfig.eventCountThreshold ?? 10)) {
        cancelBatchTimer()
        flushPendingBatch()
    } else {
        scheduleBatchTimer()
    }
}

/* ─── Core trace buffer ─────────────────────────────────────────────── */

const DEFAULT_MAX_ENTRIES = 500

let _nextId = 1
let _entries: RuntimeEventEntry[] = []
let _maxEntries = DEFAULT_MAX_ENTRIES

/**
 * Record a runtime event into the ring buffer.
 * Silently evicts the oldest entry when the cap is exceeded.
 */
export function appendRuntimeEvent(
    kind: RuntimeEventKind,
    payload: EngineManagerEventPayload | Record<string, unknown> | undefined,
    source: 'engine' | 'sqlite3mt',
): void {
    const entry: RuntimeEventEntry = {
        id: _nextId++,
        timestamp: new Date().toISOString(),
        kind,
        data: redactPayload(payload as Record<string, unknown>),
        source,
    }
    _entries.push(entry)
    if (_entries.length > _maxEntries) {
        _entries = _entries.slice(_entries.length - _maxEntries)
    }
}

/**
 * Read the current trace buffer, newest-last.
 */
export function getRuntimeEventTrace(
    options: GetRuntimeEventTraceOptions = {},
): RuntimeEventEntry[] {
    let events = _entries
    const { since, kinds, limit } = options

    if (since !== undefined) {
        events = events.filter(e => e.id > since)
    }

    if (kinds && kinds.length > 0) {
        const kindSet = new Set(kinds)
        events = events.filter(e => kindSet.has(e.kind))
    }

    const effectiveLimit = limit ?? DEFAULT_MAX_ENTRIES
    return events.slice(-effectiveLimit)
}

/**
 * §263: Flush a batch of pre-built event entries directly to the ring buffer.
 *
 * Unlike `appendRuntimeEvent` which builds the entry internally, this function accepts
 * already-built entries. Used by the incremental probe flush path (§263) so that
 * `ComponentJobPanel` can write events to the probe file without re-processing them.
 *
 * Entries are subject to the same ring-buffer eviction as `appendRuntimeEvent`.
 */
export function appendRuntimeEventBatch(entries: RuntimeEventEntry[]): void {
    for (const entry of entries) {
        _entries.push(entry)
        if (_entries.length > _maxEntries) {
            _entries = _entries.slice(_entries.length - _maxEntries)
        }
    }
    // Keep _nextId ahead of any inserted id so future auto-assigned ids stay monotonic.
    for (const entry of entries) {
        if (entry.id >= _nextId) {
            _nextId = entry.id + 1
        }
    }
}

/**
 * Clear all entries from the trace buffer.
 * Also stops any active incremental batch flush so test state does not leak.
 */
export function clearRuntimeEventTrace(): void {
    stopRuntimeEventBatchFlush()
    _entries = []
}

/**
 * Configure the maximum number of entries the ring buffer holds.
 */
export function configureRuntimeEventTrace(maxEntries: number): void {
    _maxEntries = Math.max(1, maxEntries)
    if (_entries.length > _maxEntries) {
        _entries = _entries.slice(_entries.length - _maxEntries)
    }
}

/**
 * Subscribe to all relevant EngineManager events and forward them to the trace.
 * Returns an unsubscribe function.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function subscribeEngineManagerEvents(
    engineManager: { on(event: string, listener: (payload: any) => void): () => void },
): () => void {
    const unsubs: Array<() => void> = []

    unsubs.push(
        engineManager.on('identity:changed', (payload) =>
            appendRuntimeEvent('identity:changed', payload, 'engine')),
    )
    unsubs.push(
        engineManager.on('workspace:switched', (payload) =>
            appendRuntimeEvent('workspace:switched', payload, 'engine')),
    )
    unsubs.push(
        engineManager.on('workspace:sync-status', (payload) =>
            appendRuntimeEvent('workspace:sync-status', payload, 'engine')),
    )
    unsubs.push(
        engineManager.on('workspace:deleted', (payload) =>
            appendRuntimeEvent('workspace:deleted', payload, 'engine')),
    )
    unsubs.push(
        engineManager.on('registry:sync-status', (payload) =>
            appendRuntimeEvent('registry:sync-status', payload, 'engine')),
    )

    return () => {
        for (const unsub of unsubs) unsub()
    }
}

/**
 * Install the runtime event trace by subscribing EngineManager events.
 * Exported for use in useWorkspace.tsx which calls this during engine setup.
 * sqlite3mt debug-stage forwarding is handled separately via
 * BrowserSqliteDatabaseProxy.setDebugStageListener() (§145).
 */
export function installRuntimeEventTrace(
    engineManager: EngineManagerForTrace,
): void {
    subscribeEngineManagerEvents(engineManager)
}
